import { useEffect, useRef } from 'react';

/** Converts one wheel axis delta to pixels; `deltaMode` applies to both axes on the event. */
function wheelAxisDeltaToPixels(
  delta: number,
  deltaMode: number,
  lineHeightPx: number,
  pageSizePx: number
): number {
  switch (deltaMode) {
    case WheelEvent.DOM_DELTA_LINE:
      return delta * lineHeightPx;
    case WheelEvent.DOM_DELTA_PAGE:
      return delta * pageSizePx;
    default:
      return delta;
  }
}

/**
 * Keeps wheel/trackpad scrolling on the parking-lot column: when the list overflows,
 * wheel events are handled on the column root so they do not chain to the page or
 * sibling scroll regions. Requires real DOM refs (e.g. divs), not Spectrum View refs.
 */
export function useParkingLotWheelScrollContain() {
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    const content = contentRef.current;
    if (!root || !content) return;

    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey) return;
      const target = e.target as Node | null;
      if (!target || !root.contains(target)) return;

      const { scrollTop, scrollHeight, clientHeight, clientWidth } = content;
      if (scrollHeight <= clientHeight + 1) return;

      const lineHeightPx = 16;
      const pixY = wheelAxisDeltaToPixels(
        e.deltaY,
        e.deltaMode,
        lineHeightPx,
        clientHeight
      );
      const pixX = wheelAxisDeltaToPixels(
        e.deltaX,
        e.deltaMode,
        lineHeightPx,
        clientWidth
      );

      // Vertical-only scroller: do not capture horizontal-dominant gestures (trackpad pan, tilt wheel).
      if (Math.abs(pixX) > Math.abs(pixY)) return;

      e.preventDefault();
      content.scrollTop = Math.min(
        scrollHeight - clientHeight,
        Math.max(0, scrollTop + pixY)
      );
    };

    const wheelListenerOptions: AddEventListenerOptions = {
      passive: false,
      capture: true
    };
    root.addEventListener('wheel', onWheel, wheelListenerOptions);
    return () =>
      root.removeEventListener('wheel', onWheel, wheelListenerOptions);
  }, []);

  return { rootRef, contentRef };
}
